// src/components/admin/nornsweave/ListTab.js
import React, { useState } from 'react';
import { useAdminNornsWeave } from './AdminNornsWeaveContext';
import { formatTime12 } from '../../TimePicker';

function ListTab({ onEditBooking, onDeleteBooking, onDeletePersonalEvent }) {
  const { bookings, settingsForm, personalEvents } = useAdminNornsWeave();
  const [showLibraryEvents, setShowLibraryEvents] = useState(true);

  // Combine bookings and library events into a unified list
  const allItems = [
    ...bookings.map(b => ({ ...b, itemType: 'booking' })),
    ...personalEvents.map(e => ({ ...e, itemType: 'libraryEvent' }))
  ].sort((a, b) => {
    const dateA = a.date || a.startDate;
    const dateB = b.date || b.startDate;
    return new Date(dateA) - new Date(dateB);
  });

  const filteredItems = showLibraryEvents
    ? allItems
    : allItems.filter(item => item.itemType === 'booking');

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800">All Bookings & Events</h2>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showLibraryEvents}
            onChange={(e) => setShowLibraryEvents(e.target.checked)}
            className="w-4 h-4 text-yellow-600 rounded"
          />
          <span className="text-sm text-slate-600">Show Library Events</span>
        </label>
      </div>
      {filteredItems.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-500 text-lg">No bookings or events yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredItems.map(item => {
            if (item.itemType === 'libraryEvent') {
              // Library Event card
              const startDate = item.date;
              const endDate = item.endDate || item.date;
              const isMultiDay = startDate !== endDate;
              const isAllDay = item.timeType === 'allDay';

              return (
                <div key={item.id} className="border-2 border-yellow-300 bg-yellow-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-200 text-yellow-800">
                          📖 Library Event
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          item.library === 'elementary' ? 'bg-purple-100 text-purple-700'
                          : item.library === 'middle' ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-slate-100 text-slate-700'
                        }`}>
                          {item.library === 'elementary' ? 'Elementary' : item.library === 'middle' ? 'Middle School' : 'Both Libraries'}
                        </span>
                        {isAllDay && (
                          <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
                            All Day
                          </span>
                        )}
                      </div>
                      <div className="text-lg font-semibold text-yellow-800 mb-2">{item.title}</div>
                      <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-sm">
                        <div>
                          <span className="text-slate-500">Date:</span>
                          <span className="ml-2 font-medium">
                            {isMultiDay
                              ? `${new Date(startDate + 'T00:00:00').toLocaleDateString()} - ${new Date(endDate + 'T00:00:00').toLocaleDateString()}`
                              : new Date(startDate + 'T00:00:00').toLocaleDateString()
                            }
                          </span>
                        </div>
                        {!isAllDay && item.startTime && item.endTime && (
                          <div>
                            <span className="text-slate-500">Time:</span>
                            <span className="ml-2 font-medium">{formatTime12(item.startTime)} - {formatTime12(item.endTime)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this library event?')) {
                            onDeletePersonalEvent(item.id);
                          }
                        }}
                        className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            } else {
              // Booking card (existing code)
              return (
                <div key={item.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          item.library === 'elementary' ? 'bg-purple-100 text-purple-700' : 'bg-indigo-100 text-indigo-700'
                        }`}>
                          {item.library === 'elementary' ? '📚 Elementary' : '📖 Middle School'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          item.type === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {item.type === 'admin' ? '🏢 Admin' : '👨‍🏫 Class'}
                        </span>
                        <span
                          className="px-3 py-1 rounded-full text-sm font-medium text-white"
                          style={{ backgroundColor: item.needsLibrarian ? settingsForm.colors.needsLibrarian : settingsForm.colors.independent }}
                        >
                          {item.needsLibrarian ? '👤 Needs You' : '✓ Independent'}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-sm">
                        <div><span className="text-slate-500">Teacher:</span><span className="ml-2 font-medium">{item.teacherName}</span></div>
                        <div><span className="text-slate-500">Date:</span><span className="ml-2 font-medium">{new Date(item.date).toLocaleDateString()}</span></div>
                        <div><span className="text-slate-500">Time:</span><span className="ml-2 font-medium">{formatTime12(item.startTime)} - {formatTime12(item.endTime)}</span></div>
                        {item.grade && <div><span className="text-slate-500">Grade:</span><span className="ml-2 font-medium">{item.grade}</span></div>}
                        {item.studentCount && <div><span className="text-slate-500">Students:</span><span className="ml-2 font-medium">{item.studentCount}</span></div>}
                        <div><span className="text-slate-500">Purpose:</span><span className="ml-2 font-medium">{item.purpose}</span></div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => onEditBooking(item)}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDeleteBooking(item.id)}
                        className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            }
          })}
        </div>
      )}
    </div>
  );
}

export default ListTab;
